import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CommandGroup } from '@/components/ui/command';
import { selectAccountSlug } from '@/redux/session/selectors';

import { CommandLinkItem } from '../CommandLinkItem';
import { TabHint } from './TabHint';
import { useVisiblePages } from '../hooks/useVisiblePages';

type PagesGroupProps = {
  onSelect: (path: string, label: string, opts?: { fullPath?: boolean }) => void;
  getScore?: (path: string) => number;
  maxVisible?: number;
  highlightedValue?: string;
};

export const PagesGroup = ({
  onSelect,
  getScore,
  maxVisible,
  highlightedValue,
}: PagesGroupProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const permittedPages = useVisiblePages();

  const sortedPages = useMemo(() => {
    if (!getScore) {
      return permittedPages;
    }

    const indexByPage = new Map(permittedPages.map((page, index) => [page, index]));

    return [...permittedPages].sort((a, b) => {
      const diff = getScore(b.path) - getScore(a.path);
      if (diff !== 0) {
        return diff;
      }
      // Preserve original order for pages with equal scores
      return indexByPage.get(a)! - indexByPage.get(b)!;
    });
  }, [getScore, permittedPages]);

  const visiblePages = maxVisible ? sortedPages.slice(0, maxVisible) : sortedPages;

  return (
    <CommandGroup heading={t('command_palette.pages')}>
      {visiblePages.map((page) => {
        const childLabel = t(page.i18nKey);
        const parentLabel = page.parentI18nKey ? t(page.parentI18nKey) : '';
        const label = parentLabel ? `${parentLabel} > ${childLabel}` : childLabel;
        const Icon = page.icon;
        return (
          <CommandLinkItem
            key={page.id}
            value={`page-${page.id}`}
            href={page.fullPath ? page.path : slug ? `/${slug}${page.path}` : '#'}
            onSelect={() =>
              onSelect(page.path, label, page.fullPath ? { fullPath: true } : undefined)
            }
            keywords={[childLabel, parentLabel].filter(Boolean)}
          >
            <Icon />
            <span className="flex-1">{label}</span>
            <TabHint
              value={`page-${page.id}`}
              highlightedValue={highlightedValue}
              pageDrillDown={null}
            />
          </CommandLinkItem>
        );
      })}
    </CommandGroup>
  );
};
